// @file Vue composable for managing the navigation bar visibility

import device from '@@/bits/device'
import { computed, onMounted, onUnmounted, Ref, ref, watch } from 'vue'

const OPACITY_TIMEOUT_MS = 300
const DURATION_NAVIGATION_BAR_VISIBLE_WITHOUT_INTERACTION_IN_MS = 3000
let timeoutForHidingNavigationBar: ReturnType<typeof setTimeout>

interface SlideshowNavigationVisibilityProps {
  /**
   * Needed to support navigation bar visibility on touch devices
   * which don't trigger css hover when using press and drag across the indicator.
   */
  isCurrentlyDragging: Ref<boolean>
  previousAndNextSlideControlsRef: Ref<HTMLElement | undefined>
  containerRef: Ref<HTMLElement | undefined>
  hasLoadedPages: Ref<boolean>
  isAnyOverlayOpen: Ref<boolean>
  isScreenshotMode: Ref<boolean>
}

export const useSlideshowNavigationVisibility = ({
  isCurrentlyDragging,
  previousAndNextSlideControlsRef,
  hasLoadedPages,
  isAnyOverlayOpen,
  isScreenshotMode,
}: SlideshowNavigationVisibilityProps): {
  isInertWhileNavigationBarInvisible: Ref<boolean>
  isNavigationBarVisible: Ref<boolean>
  showNavigationBarWithTimeout: () => void
  startTimeoutForHidingNavigationBar: () => void
  startShowingNavigationBarOnFocus: () => void
  classesToAutoHideElement: Ref<string[]>
} => {
  /**
   * On touch devices, the user can't move the mouse to show the navigation bar
   * so they are able to touch it while its opacity-0 without knowing what's there.
   * Thus, we block pointer events so that their first touch only toggles visibility.
   *
   * Checking isNavigationBarVisible alone is not enough,
   * it seems like there's a race condition where the event handlers are still called.
   */
  const isInertWhileNavigationBarInvisible = ref(false)
  const isNavigationBarVisibleWithTimeout = ref(true)
  const isNavigationBarVisible = computed(() => {
    if (isScreenshotMode.value) return false

    return isCurrentlyDragging.value || isAnyOverlayOpen.value || isNavigationBarVisibleWithTimeout.value
  })

  const classesToAutoHideElement = computed(() => [
    'slideshow-nav-bar-transition',
    !isNavigationBarVisible.value ? 'slideshow-nav-bar-hidden' : '',
  ])

  const showNavigationBarWithTimeoutOnMouseMove = (event: MouseEvent): void => {
    const target = event.target as HTMLElement
    // On touchable devices, the previous/next slide controls always show and
    // pressing them should not show the navigation bar
    if (device.touchable && previousAndNextSlideControlsRef.value?.contains(target) === true) {
      return
    }

    showNavigationBarWithTimeout()
  }

  const showNavigationBarWithTimeout = (): void => {
    isNavigationBarVisibleWithTimeout.value = true
    startTimeoutForHidingNavigationBar()
  }

  const hideNavigationBar = (): void => {
    isNavigationBarVisibleWithTimeout.value = false
  }

  const startTimeoutForHidingNavigationBar = (): void => {
    clearTimeout(timeoutForHidingNavigationBar)
    timeoutForHidingNavigationBar = setTimeout(
      hideNavigationBar,
      DURATION_NAVIGATION_BAR_VISIBLE_WITHOUT_INTERACTION_IN_MS,
    )
  }

  const startShowingNavigationBarOnFocus = (): void => {
    // If the user clicks on the previous/next slide controls, which are always visible on touchable devices, don't show the rest of the nav bar
    if (device.touchable && previousAndNextSlideControlsRef.value?.contains(document.activeElement) === true) {
      return
    }

    showNavigationBarWithTimeout()
  }

  watch(isNavigationBarVisible, () => {
    if (!device.touchable) return

    if (isNavigationBarVisible.value) {
      setTimeout(() => {
        isInertWhileNavigationBarInvisible.value = false
      }, OPACITY_TIMEOUT_MS)
      return
    }

    isInertWhileNavigationBarInvisible.value = true
  })

  watch(
    hasLoadedPages,
    () => {
      if (hasLoadedPages.value) {
        startTimeoutForHidingNavigationBar()
      }
    },
    { immediate: true },
  )

  onMounted(() => {
    window.addEventListener('mousemove', showNavigationBarWithTimeoutOnMouseMove)
  })

  onUnmounted(() => {
    window.removeEventListener('mousemove', showNavigationBarWithTimeoutOnMouseMove)
  })

  return {
    isInertWhileNavigationBarInvisible,
    isNavigationBarVisible,
    showNavigationBarWithTimeout,
    startTimeoutForHidingNavigationBar,
    startShowingNavigationBarOnFocus,
    classesToAutoHideElement,
  }
}
